import { useEffect, useLayoutEffect, useRef, type ReactNode, type RefObject } from "react";
import { cx, size as sizeVar, space, type SizeToken, type SpaceToken } from "./tokens.ts";

export type ScrollViewProps = {
  direction?: "vertical" | "horizontal";
  padding?: SpaceToken;
  onEndReached?: () => void;
  /** Centers the content in a column no wider than this (readable width on large windows). */
  maxContentWidth?: SizeToken;
  /** Whether the top of the content is in view (new rows' accent runs then; until then a pill stands in). */
  onAtTopChange?: (atTop: boolean) => void;
  /** The scrolling element, to jump to the top. */
  scrollRef?: RefObject<HTMLDivElement | null>;
  testID?: string;
  children?: ReactNode;
};

const AT_TOP_PX = 8;

export function ScrollView({ direction = "vertical", padding, onEndReached, maxContentWidth, onAtTopChange, scrollRef, testID, children }: ScrollViewProps) {
  const sentinel = useRef<HTMLDivElement>(null);
  const cb = useRef(onEndReached);
  useLayoutEffect(() => {
    cb.current = onEndReached;
  });
  const enabled = onEndReached !== undefined;
  useEffect(() => {
    const el = sentinel.current;
    if (el === null || !enabled) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) cb.current?.();
    });
    io.observe(el);
    return () => io.disconnect();
  }, [enabled]);
  return (
    <div ref={scrollRef} className={cx("b-scroll", direction === "horizontal" && "b-scroll-h")} data-testid={testID} onScroll={onAtTopChange === undefined ? undefined : (e) => onAtTopChange(e.currentTarget.scrollTop < AT_TOP_PX)} style={{ padding: padding === undefined ? undefined : space(padding) }}>
      {maxContentWidth === undefined ? children : <div className="b-scroll-column" style={{ maxWidth: sizeVar(maxContentWidth) }}>{children}</div>}
      <div ref={sentinel} className="b-scroll-sentinel" aria-hidden />
    </div>
  );
}
